import React from 'react';
import { format, parseISO, startOfDay } from 'date-fns';
import ActivityService from '../services/activity-service';
import FileStorageService from '../services/file-storage-service';
import { getCurrentDateObject, toScheduledAt } from './date-helper';
import { toInputDate } from './string-format-helper';
import { toStudentUuids } from './student-uuid-helper';

const maxAttachments = 10;
const maxFileSizeInMb = 10;
const maxFileSize = maxFileSizeInMb * 1024 * 1024;

export const getActivityData = (previousData?: ActivityDto) => {
  const data: ActivityFormData = {
    formData: {
      title: previousData?.title || null,
      description: previousData?.description || null,
      dueOn: previousData?.dueOn || null,
      attachments: previousData?.files || [],
    },
    classData: [],
    studentData: [],
    unmatchedStudentUuids: [],
    scheduleDate: null,
  };

  return data;
};

export const isFormComplete = (data: ActivityFormData, validations: object) =>
  Boolean(
    data.formData.title &&
      data.formData.description &&
      data.formData.dueOn &&
      (data.studentData.length > 0 || data.unmatchedStudentUuids.length > 0) &&
      Object.keys(validations).length === 0
  );

type SetData = React.Dispatch<React.SetStateAction<ActivityFormData>>;
type SetValidations = React.Dispatch<React.SetStateAction<object>>;

const setValidation = (setValidations: SetValidations, id: string, message: string | null) =>
  setValidations((current) => {
    const next = { ...current };
    if (message) next[id] = message;
    else delete next[id];
    return next;
  });

// dueOn é uma data de calendário pura ("YYYY-MM-DD"). parseISO a lê como
// meia-noite local; new Date() leria como meia-noite UTC, o dia anterior no Brasil.
export const dueOnToDate = (dueOn: string): Date => parseISO(dueOn);

export const dueOnToLabel = (dueOn: string): string => format(dueOnToDate(dueOn), 'dd/MM/yyyy');

export const deleteAttachment = (fileId: string, setData: SetData, setValidations: SetValidations) => {
  setData((current) => ({
    ...current,
    formData: {
      ...current.formData,
      attachments: current.formData.attachments.filter((attachment) => attachment.fileId !== fileId),
    },
  }));
  setValidation(setValidations, 'attachment', null);
};

export const handleStudentChange = (
  selectedClassrooms: number[],
  selectedStudents: number[],
  setData: SetData
) => {
  setData((current) => ({
    ...current,
    classData: selectedClassrooms,
    studentData: selectedStudents,
  }));
};

export interface FormChangeContext {
  data: ActivityFormData;
  setData: SetData;
  setValidations: SetValidations;
  setIsLoadingAttachment: React.Dispatch<React.SetStateAction<boolean>>;
  fileStorageService: FileStorageService;
  setToastContent: (content: ToastContent) => void;
}

const uploadAttachments = async (
  files: File[],
  { data, setData, setValidations, setIsLoadingAttachment, fileStorageService, setToastContent }: FormChangeContext
) => {
  if (files.some((file) => file.size > maxFileSize)) {
    setValidation(setValidations, 'attachment', `O tamanho máximo de cada anexo é ${maxFileSizeInMb}MB.`);
    return;
  }

  if (data.formData.attachments.length + files.length > maxAttachments) {
    setValidation(setValidations, 'attachment', `Quantidade máxima de anexos é ${maxAttachments}.`);
    return;
  }

  setValidation(setValidations, 'attachment', null);
  setIsLoadingAttachment(true);

  try {
    const uploaded: ActivityFileDto[] = await Promise.all(
      files.map(async (file) => ({ fileId: await fileStorageService.uploadFile(file), name: file.name }))
    );

    setData((current) => ({
      ...current,
      formData: { ...current.formData, attachments: current.formData.attachments.concat(uploaded) },
    }));
  } catch {
    setToastContent({ isOpen: true, text: 'Houve um erro ao enviar os anexos.', type: 'error' });
  } finally {
    setIsLoadingAttachment(false);
  }
};

const dueOnValidation = (dueOn: string): string | null => {
  if (!dueOn) return 'Campo obrigatório.';
  if (dueOnToDate(dueOn).getTime() < getCurrentDateObject().getTime())
    return 'Não é possível cadastrar atividades para datas passadas.';

  return null;
};

// Toda atualização de estado aqui é funcional: o upload leva tempo, e o que foi
// digitado ou selecionado nesse meio-tempo não pode ser sobrescrito pelo estado
// de quando o arquivo foi escolhido.
export const handleFormOnChange = async (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  context: FormChangeContext
) => {
  const { id, value, type } = e.target;

  if (type !== 'file') {
    const message = id === 'dueOn' ? dueOnValidation(value) : value ? null : 'Campo obrigatório.';
    setValidation(context.setValidations, id, message);
    context.setData((current) => ({ ...current, formData: { ...current.formData, [id]: value } }));
    return;
  }

  // O Form também chama no blur; limpar o input deixa escolher o mesmo arquivo de novo.
  if (e.type === 'blur') {
    e.target.value = null;
    return;
  }

  if (!value) return;

  await uploadAttachments(Array.from((e.target as HTMLInputElement).files), context);
};

const handleScheduleDateSelect = (
  evt: React.ChangeEvent<HTMLInputElement>,
  data: ActivityFormData,
  setData: SetData,
  submitPromise: (submitData: ActivityFormData) => Promise<string | void>,
  setModalContent: (content: ModalContent) => any
) => {
  const scheduleDate = evt.target.value ? toScheduledAt(evt.target.value) : null;

  setData((current) => ({ ...current, scheduleDate }));

  createSetModalContent({ ...data, scheduleDate }, submitPromise, setData, setModalContent);
};

export const createSetModalContent = (
  data: ActivityFormData,
  submitPromise: (submitData: ActivityFormData) => Promise<string | void>,
  setData: SetData,
  setModalContent: (content: ModalContent) => any
) => {
  const submitSchedulePromise = (submitData: ActivityFormData) => {
    if (!submitData.scheduleDate) {
      return Promise.reject('Selecione uma data para o agendamento.');
    }

    const selectDate = startOfDay(parseISO(submitData.scheduleDate)).getTime();
    const currentDate = getCurrentDateObject().getTime();
    const activityDate = dueOnToDate(submitData.formData.dueOn).getTime();

    if (selectDate < currentDate) {
      return Promise.reject(
        'Não é possível agendar atividades para datas passadas.'
      );
    } else if (selectDate >= activityDate) {
      return Promise.reject(
        'O Agendamento deve ocorrer ao menos um dia antes da atividade.'
      );
    } else {
      return submitPromise(submitData);
    }
  };

  const resetScheduleDate = () => {
    setData((current) => ({ ...current, scheduleDate: null }));

    return Promise.resolve();
  };

  setModalContent({
    title: 'Selecione uma data para agendar o envio',
    modalContent: React.createElement('input', {
      type: 'date',
      name: 'schedule',
      id: 'schedule',
      min: toInputDate(new Date()),
      max: '2999-12-31',
      onChange: (evt) =>
        handleScheduleDateSelect(
          evt,
          data,
          setData,
          submitPromise,
          setModalContent
        ),
    }),
    submitButtonTitle: 'agendar',
    submitButtonType: 'primary',
    submitData: data,
    onSubmitButtonClick: submitSchedulePromise,
    onCloseModal: resetScheduleDate,
    isOpen: true,
  });
};

export const deleteActivity = (
  activityService: ActivityService,
  activityId: string,
  setModalContent: (content: ModalContent) => any,
  setToastContent: (content: ToastContent) => void,
  navigate: any
) => {
  setModalContent({
    title: 'Deletar atividade',
    modalContent: React.createElement(
      'p',
      null,
      'Deseja realmente deletar a atividade?'
    ),
    submitButtonTitle: 'confirmar deleção',
    submitButtonType: 'primary',
    submitData: null,
    onSubmitButtonClick: () =>
      activityService
        .deleteActivity(activityId)
        .then(() => {
          setToastContent({
            isOpen: true,
            text: 'Atividade deletada!',
            type: 'success',
          });
          navigate('/atividades/listar');
        })
        .catch(() => {
          setToastContent({
            isOpen: true,
            text: 'Houve um erro ao deletar a atividade.',
            type: 'error',
          });
        }),
    isOpen: true,
  });
};

export const buildActivityRequest = (
  data: ActivityFormData,
  classroomList: ClassroomStudents[]
): ActivityRequest => ({
  title: data.formData.title,
  description: data.formData.description,
  dueOn: data.formData.dueOn,
  scheduledAt: data.scheduleDate || null,
  fileIds: data.formData.attachments.map((attachment) => attachment.fileId),
  studentUuids: toStudentUuids(data.studentData, data.unmatchedStudentUuids, classroomList),
});

// O BFF resolve os nomes no servidor. `seen` é cumulativo (quem concluiu também
// viu), então as duas colunas são independentes.
export const backofficeRecipientsToEngagementRows = (
  recipients: BackofficeActivityRecipientDto[]
): InfosDataListRow[][] => {
  return recipients.map((recipient) => [
    { text: recipient.student.name || '—' },
    { text: recipient.classroom?.name || '—' },
    { text: recipient.responsible?.name || '—' },
    {
      text: recipient.seen ? 'Sim' : 'Não',
      customStyle: { textAlign: 'center' },
    },
    {
      text: recipient.done ? 'Sim' : 'Não',
      customStyle: { textAlign: 'center' },
    },
  ]);
};

export const filesToAttachmentDisplay = (files: ActivityFileDto[]): AttachmentDisplay[] =>
  files.map((file) => ({ location: file.url, originalName: file.name }));
